export const MAP_DAYS = { MONDAY: "Montag", TUESDAY: "Dienstag", WEDNESDAY: "Mittwoch", THURSDAY: "Donnerstag", FRIDAY: "Freitag" };
const COLORS = ["#007f8b", "#bd3b37", "#5149ac", "#926100", "#2767ac", "#a02d84", "#3c7c32", "#ad491d", "#36636e", "#7955a0", "#806120", "#b33559"];
export const validMapPoint = point => Array.isArray(point) && point.length >= 2 && point.slice(0, 2).every(Number.isFinite)
  && Math.abs(point[0]) <= 90 && Math.abs(point[1]) <= 180 && !(point[0] === 0 && point[1] === 0);
export const stopPoint = stop => [stop.location?.lat, stop.location?.lon];
export function busMapColor(busId) {
  let hash = 0;
  for (const char of String(busId)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return COLORS[hash % COLORS.length];
}
export function mapLink(tour) {
  const params = new URLSearchParams({ bus: String(tour.busId), direction: tour.richtung });
  if (tour.days?.length) params.set("days", tour.days.join(","));
  else if (tour.tag) params.set("days", tour.tag);
  return `/app/maps?${params.toString()}`;
}
export function filterMapTours(tours, { busId, days, direction }) {
  return tours.filter(tour => (!busId || String(tour.busId) === String(busId))
    && (!days?.length || days.includes(tour.tag)) && (!direction || tour.richtung === direction));
}
// Several weekdays often use exactly the same schedule. Render that pattern once.
export function groupMapTours(tours) {
  const groups = new Map();
  for (const tour of tours) {
    const signature = JSON.stringify([String(tour.busId), tour.richtung, tour.startTime, tour.endTime,
      tour.distanceKm, tour.durationHours, tour.totalCost, tour.geometry || null, tour.weeklyPlanningNotes,
      (tour.stops || []).map(stop => [stop.type, stop.passengerId, stop.passengerName,
        stop.address || stop.location?.name, ...stopPoint(stop), stop.scheduledTime])]);
    if (!groups.has(signature)) groups.set(signature, { ...tour, key: `map-${groups.size}`, days: [], color: busMapColor(tour.busId) });
    const group = groups.get(signature);
    if (!group.days.includes(tour.tag)) group.days.push(tour.tag);
  }
  return [...groups.values()].map(group => ({ ...group, days: group.days.sort((a, b) => Object.keys(MAP_DAYS).indexOf(a) - Object.keys(MAP_DAYS).indexOf(b)) }));
}
export function mapSegments(tour) {
  if (Array.isArray(tour.geometry?.segments) && tour.geometry.segments.length) return tour.geometry.segments
    .filter(segment => Array.isArray(segment.coordinates) && segment.coordinates.length >= 2 && segment.coordinates.every(validMapPoint))
    .map(segment => ({ ...segment, road: segment.road === true }));
  const segments = [];
  const stops = tour.stops || [];
  for (let index = 1; index < stops.length; index++) {
    const from = stopPoint(stops[index - 1]), to = stopPoint(stops[index]);
    if (validMapPoint(from) && validMapPoint(to)) segments.push({ coordinates: [from, to], road: false });
  }
  return segments;
}
export const mapDayLabel = days => {
  const order = Object.keys(MAP_DAYS);
  const sorted = [...new Set(days)].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const ranges = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = i;
    while (i + 1 < sorted.length && order.indexOf(sorted[i]) >= 0 && order.indexOf(sorted[i + 1]) === order.indexOf(sorted[i]) + 1) i++;
    ranges.push(i - start >= 2 ? `${MAP_DAYS[sorted[start]]} bis ${MAP_DAYS[sorted[i]]}`
      : sorted.slice(start, i + 1).map(day => MAP_DAYS[day] || day).join(", "));
  }
  return ranges.join(", ");
};

export function sortMapTours(tours) {
  return [...tours].sort((a, b) => String(a.busName || a.busId).localeCompare(String(b.busName || b.busId), "de", { numeric: true })
    || String(a.richtung).localeCompare(String(b.richtung), "de")
    || Object.keys(MAP_DAYS).indexOf(a.days?.[0] || a.tag) - Object.keys(MAP_DAYS).indexOf(b.days?.[0] || b.tag)
    || String(a.startTime || "").localeCompare(String(b.startTime || "")));
}
const searchText = value => String(value || "").toLocaleLowerCase("de").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ß/g, "ss");
export function searchMapTours(tours, query) {
  const terms = searchText(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return tours;
  return tours.filter(tour => {
    const text = searchText([tour.busName, tour.busId, tour.depotAddress, tour.depotName,
      ...(tour.days || [tour.tag]).map(day => MAP_DAYS[day] || day),
      tour.richtung === "HIN" ? "Hinfahrt" : "Rückfahrt",
      ...(tour.stops || []).flatMap(stop => [stop.passengerName, stop.address, stop.location?.name]),
    ].join(" "));
    return terms.every(term => text.includes(term));
  });
}
export function selectMapTours(tours, selectedKey) {
  const selected = selectedKey ? tours.filter(tour => tour.key === selectedKey) : [];
  return selected.length ? selected : tours;
}

export function resultsLink(tour) {
  const params = new URLSearchParams({ bus: String(tour.busId), direction: tour.richtung });
  const day = tour.days?.[0] || tour.tag;
  if (day) params.set("day", day);
  return `/app/results?${params.toString()}`;
}
export function matchesResultTour(tour, target) {
  return Boolean(target.bus && target.direction && target.day)
    && String(tour.busId) === String(target.bus) && tour.richtung === target.direction
    && (tour.days || [tour.tag]).includes(target.day);
}
